import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Table } from "react-bootstrap";
import axiosApi from "../../../utilities/axiosApi";
import CommonLayout from "../../layouts/master/index";

const STATUS_LOOK = {
  draft: { bg: "secondary", label: "Draft" },
  pending: { bg: "warning", label: "Awaiting WhatsApp" },
  approved: { bg: "success", label: "Approved" },
  rejected: { bg: "danger", label: "Rejected" },
  paused: { bg: "warning", label: "Paused by WhatsApp" },
  disabled: { bg: "danger", label: "Disabled by WhatsApp" },
};

// Imported templates can carry any of Meta's header formats, not only the two
// this page lets a customer author.
const ATTACHMENT_LABEL = {
  NONE: "None",
  TEXT: "Text heading",
  DOCUMENT: "PDF",
  IMAGE: "Image",
  VIDEO: "Video",
};

const TITLES = [{ name: "WhatsApp Templates", path: "/whatsapp-templates" }];

// Meta's language codes. Meta reviews the wording against the language
// declared, so Gujarati text sent as English is likely to be rejected.
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "gu", label: "Gujarati" },
  { code: "hi", label: "Hindi" },
  { code: "mr", label: "Marathi" },
];

// No placeholder is filled in up front: a message without {{1}} must be
// savable, and the API refuses a variable the message does not use.
const BLANK = {
  display_name: "",
  body_text: "",
  header_type: "DOCUMENT",
  language: "en",
  variable_map: {},
};

/**
 * A customer's own WhatsApp message templates.
 *
 * Only reachable when the account is enabled for the official number — a
 * campaign sent from the customer's own number carries whatever text they
 * typed, so there is nothing to approve.
 *
 * The page is mostly about making Meta's rules legible. A template cannot be
 * edited once it has been sent for approval, and any wording change after
 * approval means a new template, so the UI has to say so rather than let
 * someone discover it from a failed save.
 */
export default function TemplateList() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // One sample PDF per template row, so a file chosen on one row is never
  // submitted as another template's sample.
  const [samples, setSamples] = useState({});

  const load = useCallback(() => {
    setLoading(true);

    axiosApi
      .get("/api/customer/whatsapp-templates")
      .then((res) => {
        if (res?.data?.success) {
          setTemplates(res.data.data || []);
        } else {
          setNotice({ type: "danger", text: res?.data?.message });
        }
      })
      .catch(() => setNotice({ type: "danger", text: "Could not load templates." }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const placeholders = (body) => {
    const found = (body || "").match(/\{\{\s*\d+\s*\}\}/g) || [];
    return [...new Set(found.map((p) => parseInt(p.replace(/\D/g, ""), 10)))].sort(
      (a, b) => a - b
    );
  };

  const save = () => {
    setSaving(true);
    setNotice(null);

    const url = editing.id
      ? `/api/customer/whatsapp-templates/${editing.id}`
      : "/api/customer/whatsapp-templates";

    // Keep only the blanks the message still uses. Deleting {{2}} from the
    // text would otherwise leave its answer behind, which the API refuses.
    const used = placeholders(editing.body_text);
    const variable_map = Object.fromEntries(
      Object.entries(editing.variable_map || {}).filter(([position]) =>
        used.includes(parseInt(position, 10))
      )
    );

    axiosApi
      .post(url, { ...editing, variable_map })
      .then((res) => {
        if (res?.data?.success) {
          setEditing(null);
          load();
        } else {
          // The API returns every problem at once, so the customer can fix
          // them in one pass rather than one save per mistake.
          setNotice({ type: "danger", text: res?.data?.message });
        }
      })
      .catch(() => setNotice({ type: "danger", text: "Could not save the template." }))
      .finally(() => setSaving(false));
  };

  const submit = (template) => {
    const form = new FormData();
    if (samples[template.id]) form.append("sample", samples[template.id]);

    setSaving(true);

    axiosApi
      .post(`/api/customer/whatsapp-templates/${template.id}/submit`, form)
      .then((res) => {
        setNotice({
          type: res?.data?.success ? "success" : "danger",
          text: res?.data?.message,
        });
        setSamples(({ [template.id]: _submitted, ...rest }) => rest);
        load();
      })
      .catch(() => setNotice({ type: "danger", text: "Could not submit the template." }))
      .finally(() => setSaving(false));
  };

  const remove = (template) => {
    axiosApi
      .delete(`/api/customer/whatsapp-templates/${template.id}`)
      .then(() => load())
      .catch(() => setNotice({ type: "danger", text: "Could not delete the template." }));
  };

  const editable = (t) => t.customer_id && ["draft", "rejected"].includes(t.status);

  return (
    <CommonLayout titles={TITLES}>
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">WhatsApp Templates</h4>
            <p className="text-muted mb-0">
              Messages sent from our official number must use wording WhatsApp has
              approved in advance.
            </p>
          </div>
          <Button onClick={() => setEditing({ ...BLANK })}>New template</Button>
        </div>

        {notice && (
          <div className={`alert alert-${notice.type}`} role="alert">
            {notice.text}
          </div>
        )}

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : (
          <Table responsive className="align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Message</th>
                <th>Attachment</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted text-center py-4">
                    No templates yet.
                  </td>
                </tr>
              )}

              {templates.map((t) => {
                const look = STATUS_LOOK[t.status] || { bg: "secondary", label: t.status };

                return (
                  <tr key={t.id}>
                    <td>
                      {t.display_name}
                      {!t.customer_id && (
                        <Badge bg="light" text="dark" className="ms-2">
                          Shared
                        </Badge>
                      )}
                      <div className="small text-muted">
                        {LANGUAGES.find((l) => l.code === t.language)?.label || t.language}
                      </div>
                    </td>
                    <td className="text-muted small" style={{ maxWidth: 360 }}>
                      {t.body_text}
                    </td>
                    <td>{ATTACHMENT_LABEL[t.header_type] || t.header_type}</td>
                    <td>
                      <Badge bg={look.bg}>{look.label}</Badge>
                      {t.status === "rejected" && t.rejection_reason && (
                        <div className="small text-danger mt-1">{t.rejection_reason}</div>
                      )}
                    </td>
                    <td className="text-end">
                      {editable(t) && (
                        <>
                          <Button size="sm" variant="link" onClick={() => setEditing(t)}>
                            Edit
                          </Button>
                          {t.header_type === "DOCUMENT" && (
                            <Form.Control
                              type="file"
                              accept="application/pdf"
                              size="sm"
                              className="d-inline-block w-auto me-2"
                              onChange={(e) => {
                              const file = e.target.files[0];
                              setSamples((current) => ({ ...current, [t.id]: file }));
                            }}
                            />
                          )}
                          <Button
                            size="sm"
                            disabled={saving}
                            onClick={() => submit(t)}
                          >
                            Send for approval
                          </Button>
                          <Button size="sm" variant="link" className="text-danger" onClick={() => remove(t)}>
                            Delete
                          </Button>
                        </>
                      )}
                      {t.customer_id && t.status === "pending" && (
                        <span className="text-muted small">Waiting on WhatsApp</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <Modal show={!!editing} onHide={() => setEditing(null)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{editing?.id ? "Edit template" : "New template"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editing && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={editing.display_name}
                    onChange={(e) =>
                      setEditing({ ...editing, display_name: e.target.value })
                    }
                    placeholder="Wedding invite"
                  />
                  <Form.Text muted>Only you see this name.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editing.body_text}
                    onChange={(e) => setEditing({ ...editing, body_text: e.target.value })}
                    placeholder="Hello {{1}}, you are invited to {{2}}. See you there!"
                  />
                  <Form.Text muted>
                    Use {"{{1}}"}, {"{{2}}"} for the parts that change per guest.
                    The message cannot begin or end with one, and two cannot follow
                    each other.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Language of the message</Form.Label>
                  <Form.Select
                    value={editing.language || "en"}
                    onChange={(e) => setEditing({ ...editing, language: e.target.value })}
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text muted>
                    WhatsApp checks the wording against this language. Pick the one
                    the message is written in.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Attachment</Form.Label>
                  <Form.Select
                    value={editing.header_type}
                    onChange={(e) =>
                      setEditing({ ...editing, header_type: e.target.value })
                    }
                  >
                    <option value="DOCUMENT">A personalised PDF invitation</option>
                    <option value="NONE">Text only</option>
                  </Form.Select>
                </Form.Group>

                {placeholders(editing.body_text).length > 0 && (
                  <Form.Group>
                    <Form.Label>What goes in each placeholder</Form.Label>
                    {placeholders(editing.body_text).map((position) => (
                      <div key={position} className="d-flex align-items-center gap-2 mb-2">
                        <code className="flex-shrink-0">{`{{${position}}}`}</code>
                        <Form.Control
                          value={editing.variable_map?.[position] || ""}
                          placeholder="name"
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              variable_map: {
                                ...editing.variable_map,
                                [position]: e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    ))}
                    <Form.Text muted>
                      Use <code>name</code> for the guest's name, or any column from
                      your guest list.
                    </Form.Text>
                  </Form.Group>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save draft"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </CommonLayout>
  );
}
